import React, { useState } from "react";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { formatDateChip, fromDateInputValue, toDateInputValue } from "../../utils/siteChartUtils";

const GRANULARITY_OPTIONS = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
];

const METRIC_OPTIONS = [
  { label: "Inspection Count", value: "inspectionCount" },
  { label: "Avg Score", value: "score" },
  { label: "Finding Count", value: "findingCount" },
];

const TOP_N_OPTIONS = [
  { label: "Top 5", value: 5 },
  { label: "Top 10", value: 10 },
  { label: "Top 20", value: 20 },
];

const FILTER_KEY_MAP = {
  inspectionType: "Inspection Type",
  site: "Site",
  obrOrg: "OBR Org",
  obrBu: "OBR Bu",
};

const ChartFilterBar = ({ fields = [], values, onChange, onApply, filterOptions = [] }) => {
  const [open, setOpen] = useState(false);

  const toggleTag = (tag, checked) => {
    const next = checked
      ? [...(values.selectedTags || []), tag]
      : (values.selectedTags || []).filter((item) => item.value !== tag.value || item.key !== tag.key);
    onChange({ ...values, selectedTags: next });
  };

  const isTagChecked = (tag) =>
    (values.selectedTags || []).some((item) => item.value === tag.value && item.key === tag.key);

  const getTagsForKey = (key) => {
    const name = FILTER_KEY_MAP[key];
    const group = filterOptions.find((f) => f.name === name);
    return group?.tags || [];
  };

  const handleDateChange = (part, raw) => {
    const parsed = fromDateInputValue(raw);
    onChange({
      ...values,
      dates: { ...values.dates, [part]: parsed },
    });
  };

  const chips = [];
  if (fields.includes("date") && values.dates) {
    chips.push(formatDateChip(values.dates.start, values.dates.end));
  }
  if (fields.includes("granularity") && values.granularity) {
    chips.push(GRANULARITY_OPTIONS.find((o) => o.value === values.granularity)?.label || values.granularity);
  }
  if (fields.includes("metric") && values.metric) {
    chips.push(METRIC_OPTIONS.find((o) => o.value === values.metric)?.label || values.metric);
  }
  if (fields.includes("topN") && values.topN) {
    chips.push(`Top ${values.topN}`);
  }
  (values.selectedTags || []).forEach((tag) => chips.push(tag.name));

  const multiFields = fields.filter((f) => FILTER_KEY_MAP[f]);

  return (
    <div className="site-chart-filter">
      <div className="site-chart-filter__header">
        {chips.length > 0 && (
          <div className="site-chart-filter__chips">
            {chips.slice(0, 4).map((chip, i) => (
              <span key={i} className="site-chart-filter__chip">
                {chip}
              </span>
            ))}
            {chips.length > 4 && <span className="site-chart-filter__chip">+{chips.length - 4}</span>}
          </div>
        )}
        <button type="button" className="site-chart-filter__btn" onClick={() => setOpen(!open)} aria-label="Chart filters">
          <FilterListOutlinedIcon fontSize="small" />
          {(values.selectedTags?.length > 0 || open) && <span className="site-chart-filter__dot" />}
        </button>
      </div>

      {open && (
        <>
          <div className="site-chart-filter__panel">
            {fields.includes("date") && (
              <div className="site-chart-filter__group">
                <label className="site-chart-filter__label">Date Range</label>
                <div className="site-chart-filter__date-row">
                  <input
                    type="date"
                    className="site-chart-filter__input"
                    value={toDateInputValue(values.dates?.start)}
                    onChange={(e) => handleDateChange("start", e.target.value)}
                  />
                  <span>to</span>
                  <input
                    type="date"
                    className="site-chart-filter__input"
                    value={toDateInputValue(values.dates?.end)}
                    onChange={(e) => handleDateChange("end", e.target.value)}
                  />
                </div>
              </div>
            )}

            {fields.includes("granularity") && (
              <div className="site-chart-filter__group">
                <label className="site-chart-filter__label">Granularity</label>
                <select
                  className="site-chart-filter__select"
                  value={values.granularity || "weekly"}
                  onChange={(e) => onChange({ ...values, granularity: e.target.value })}
                >
                  {GRANULARITY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {fields.includes("metric") && (
              <div className="site-chart-filter__group">
                <label className="site-chart-filter__label">Metric</label>
                <select
                  className="site-chart-filter__select"
                  value={values.metric || "inspectionCount"}
                  onChange={(e) => onChange({ ...values, metric: e.target.value })}
                >
                  {METRIC_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {fields.includes("topN") && (
              <div className="site-chart-filter__group">
                <label className="site-chart-filter__label">Top N</label>
                <select
                  className="site-chart-filter__select"
                  value={values.topN || 10}
                  onChange={(e) => onChange({ ...values, topN: Number(e.target.value) })}
                >
                  {TOP_N_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {multiFields.map((key) => {
              const tags = getTagsForKey(key);
              if (!tags.length) return null;
              return (
                <div key={key} className="site-chart-filter__group">
                  <label className="site-chart-filter__label">{FILTER_KEY_MAP[key]}</label>
                  <FormGroup className="site-chart-filter__checks">
                    {tags.map((tag) => (
                      <FormControlLabel
                        key={`${key}-${tag.value}`}
                        control={
                          <Checkbox
                            size="small"
                            checked={isTagChecked(tag)}
                            onChange={(e) => toggleTag(tag, e.target.checked)}
                          />
                        }
                        label={tag.name}
                      />
                    ))}
                  </FormGroup>
                </div>
              );
            })}

            <div className="site-chart-filter__actions">
              <button
                type="button"
                className="site-chart-filter__action site-chart-filter__action--ghost"
                onClick={() => {
                  onChange({
                    ...values,
                    selectedTags: [],
                  });
                }}
              >
                Reset
              </button>
              <button
                type="button"
                className="site-chart-filter__action"
                onClick={() => {
                  onApply(values);
                  setOpen(false);
                }}
              >
                Apply
              </button>
            </div>
          </div>
          <div className="site-chart-filter__overlay" onClick={() => setOpen(false)} />
        </>
      )}
    </div>
  );
};

export default ChartFilterBar;
